import { X, Lightbulb, Trash2, Loader, FolderOpen, Copy, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { API_URL } from '../config';

interface SavedObjective {
  id: string;
  name: string;
  objective: string;
  timestamp: string;
}

interface PromptifyPanelProps {
  onClose: () => void;
  onUseInGenerate: (prompt: string) => void;
}

export default function PromptifyPanel({ onClose, onUseInGenerate }: PromptifyPanelProps) {
  const [objective, setObjective] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [savedObjectives, setSavedObjectives] = useState<SavedObjective[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'name'>('date');

  useEffect(() => {
    loadSavedObjectives();
  }, []);

  const loadSavedObjectives = () => {
    const saved = localStorage.getItem('pigna-flow-promptify-objectives');
    if (saved) {
      try {
        const objectives = JSON.parse(saved);
        setSavedObjectives(objectives);
      } catch (e) {
        console.error('Errore caricamento obiettivi:', e);
        setSavedObjectives([]);
      }
    }
  };

  const saveObjective = () => {
    if (!objective.trim()) {
      alert('❌ Scrivi prima un obiettivo!');
      return;
    }
    const name = prompt('💾 Nome per questo obiettivo:', objective.substring(0, 50) + (objective.length > 50 ? '...' : ''));
    if (!name) return;
    
    const newObjective: SavedObjective = {
      id: `obj-${Date.now()}`,
      name: name.trim(),
      objective: objective.trim(),
      timestamp: new Date().toISOString()
    };
    
    const updated = [...savedObjectives, newObjective];
    setSavedObjectives(updated);
    localStorage.setItem('pigna-flow-promptify-objectives', JSON.stringify(updated));
    alert(`✅ Obiettivo "${name}" salvato!`);
  };

  const deleteObjective = (id: string, name: string) => {
    if (confirm(`⚠️ Eliminare l'obiettivo "${name}"?`)) {
      const updated = savedObjectives.filter(o => o.id !== id);
      setSavedObjectives(updated);
      localStorage.setItem('pigna-flow-promptify-objectives', JSON.stringify(updated));
      alert(`✅ Obiettivo "${name}" eliminato!`);
    }
  };

  const loadObjective = (objectiveText: string) => {
    setObjective(objectiveText);
  };

  const buildPromptFromObjective = (obj: string) => {
    return `OBIETTIVO: ${obj.trim()}\n\nTrasforma l'OBIETTIVO in una descrizione operativa per OYSTAR Flow Studio, seguendo questa struttura:\n\nScopo\n- ...\n\nNodi da usare (in ordine di priorità):\n- start: messaggio iniziale\n- input: per raccogliere dati dall'utente\n  ⚠️ IMPORTANTE: Se usi API_CALL, le variabili INPUT devono avere GLI STESSI NOMI dei parametri API!\n  Esempio: se API vuole "date", usa variableName="date" (NON "birth_date")\n- api_call: SE ESISTE un servizio disponibile, usalo! Specifica quale servizio, endpoint, parametri da passare\n- action: SOLO se non esiste un servizio, per calcoli/trasformazioni custom\n- say: messaggi del bot (con {{variabili}})\n- condition/case: decisioni binarie/multiple\n- rag (se necessario): ricerca semantica\n- end: messaggio finale\n\n⚠️ IMPORTANTE: Controlla i SERVIZI API DISPONIBILI prima di proporre ACTION!\n\nLogica\n- Flusso conversazione\n- Branch condizionali\n- Gestione errori\n\nMessaggi\n- Testi esatti per ogni nodo SAY\n- Prompt per ogni INPUT\n- Messaggi finali\n\nAPI_CALL (se applicabile)\n- Quale servizio usare (nome esatto)\n- Endpoint e metodo\n- Parametri richiesti (NOMI ESATTI dall'API)\n- ⚠️ CRITICO: Assicurati che le variabili INPUT abbiano gli STESSI NOMI dei parametri API\n- Campi da estrarre dalla risposta\n- Come mostrare risultato con {{variabili}}\n\nRAG (se abilitato)\n- enabled: true/false\n- indexNames: [...]\n- topK: numero\n- query template: ...\n\nTest rapidi\n- Scenari di test\n- Edge cases`;
  };

  const handleGenerate = async () => {
    if (!objective.trim()) {
      alert('❌ Inserisci un obiettivo!');
      return;
    }
    
    setIsGenerating(true);
    setGeneratedPrompt('');
    
    try {
      // Fetch available services from backend
      let servicesContext = '';
      try {
        console.log(`🔌 Fetching services from ${API_URL}/services/list...`);
        const servicesResponse = await fetch(`${API_URL}/services/list`);
        console.log('📡 Services response status:', servicesResponse.status);
        
        if (servicesResponse.ok) {
          const servicesData = await servicesResponse.json();
          console.log('📦 Services data:', servicesData);
          
          if (servicesData.services && servicesData.services.length > 0) {
            servicesContext = '\n\n🔌 SERVIZI API DISPONIBILI:\n';
            for (const service of servicesData.services) {
              const visibleParams = service.input_parameters?.filter((p: any) => !p.hidden) || [];
              servicesContext += `\n- **${service.name}** (${service.category}):\n`;
              servicesContext += `  Descrizione: ${service.description}\n`;
              servicesContext += `  Endpoint: ${service.endpoint.method} ${service.endpoint.url}\n`;
              if (visibleParams.length > 0) {
                servicesContext += `  Parametri: ${visibleParams.map((p: any) => p.name).join(', ')}\n`;
              }
              servicesContext += `  Output: ${Object.keys(service.output_fields || {}).join(', ')}\n`;
            }
            servicesContext += '\n⚠️ IMPORTANTE: Se l\'obiettivo può essere realizzato con uno di questi servizi, SPECIFICALO nella descrizione usando un nodo API_CALL invece di ACTION!\n';
            servicesContext += '💡 NOMI VARIABILI: I nodi INPUT devono salvare in variabili con gli STESSI NOMI dei parametri API (es: se API vuole "date", usa variableName="date", NON "birth_date")!\n';
            console.log('✅ Services context prepared, length:', servicesContext.length);
          } else {
            console.warn('⚠️ No services found in response');
          }
        } else {
          console.error('❌ Services response not OK:', servicesResponse.statusText);
        }
      } catch (e) {
        console.error('❌ Error fetching services:', e);
      }

      const customPromptifyPrompt = localStorage.getItem('pigna-flow-promptify-prompt');
      const DEFAULT_PROMPTIFY_SYSTEM_PROMPT = `Sei un Prompt Engineer per OYSTAR Flow Studio.\nDato un OBIETTIVO in linguaggio naturale, produci una DESCRIZIONE OPERATIVA concisa e completa, pronta da incollare nel campo "Genera".\n\n🎯 PRIORITÀ NELL'USO DEI NODI:\n1. **API_CALL** - Se esiste un servizio disponibile per quello che serve, USALO!\n2. **ACTION** - Solo se NON esiste un servizio, usa un nodo ACTION\n3. **INPUT** - Per raccogliere dati dall'utente\n4. **SAY** - Per messaggi al bot\n5. **CONDITION/CASE** - Per decisioni basate su variabili esistenti\n6. **RAG** - Per ricerca semantica nella knowledge base\n\n📝 FORMATO OUTPUT:\n- In ITALIANO, senza preamboli.\n- Definisci: Scopo, Nodi (start, say, input, api_call se applicabile, rag, condition, case, action, end), Logica e rami, Messaggi esatti, RAG (se serve: enabled, indexNames, topK, query template), Criteri di chiusura.\n- Se usi API_CALL, specifica: quale servizio, quali parametri INPUT servono prima, come estrarre i campi dalla risposta\n- Evita JSON/markup; sezioni testuali.\n- Dai stringhe pronte per messaggi e condizioni.\nOutput: SOLO la descrizione.`;
      
      // SEMPRE appendi i servizi, anche se c'è un custom prompt
      const basePrompt = customPromptifyPrompt || DEFAULT_PROMPTIFY_SYSTEM_PROMPT;
      const systemPrompt = basePrompt + servicesContext;
      const userPrompt = buildPromptFromObjective(objective);
      
      console.log('🎯 System prompt length:', systemPrompt.length);
      console.log('📝 System prompt includes services?', systemPrompt.includes('SERVIZI API DISPONIBILI'));
      console.log('🔍 Services context in prompt:', servicesContext ? 'YES ✅' : 'NO ❌');
      
      const response = await fetch(`${API_URL}/flow/promptify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          objective: userPrompt,
          system_prompt: systemPrompt,
          temperature: 0.2,
          max_tokens: 1200,
        }),
      });
      
      if (!response.ok || !response.body) {
        const errorText = await response.text();
        throw new Error(errorText || 'Errore LLM');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let aggregated = '';
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() || '';
        
        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line || line === 'data: [DONE]' || line === '[DONE]') continue;
          let jsonText = line;
          if (jsonText.startsWith('data:')) jsonText = jsonText.slice(5).trim();
          
          try {
            const obj = JSON.parse(jsonText);
            const choice = obj.choices?.[0];
            const delta = choice?.delta;
            const content = delta?.content;
            if (typeof content === 'string') {
              aggregated += content;
              setGeneratedPrompt(prev => prev + content);
            }
          } catch {
            // ignore non-JSON lines
          }
        }
      }
    } catch (e: any) {
      console.error('Errore promptify:', e);
      alert(`❌ Errore: ${e.message || e}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(generatedPrompt);
      alert('✅ Prompt copiato negli appunti!');
    } catch {
      alert('❌ Impossibile copiare');
    }
  };

  const useInGenerate = () => {
    if (!generatedPrompt.trim()) {
      alert('❌ Nessun prompt generato');
      return;
    }
    onUseInGenerate(generatedPrompt.trim());
  };

  const sortedObjectives = [...savedObjectives].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    } else {
      return a.name.localeCompare(b.name);
    }
  });

  return (
    <div className="w-[600px] bg-white border-l-4 border-amber-300 flex flex-col overflow-hidden shadow-2xl h-full">
      {/* Header */}
      <div className="p-6 border-b-2 border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-500" />
            Suggerisci Prompt con AI
          </h2>
          <button
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600 text-white p-2 rounded-lg transition"
            title="Chiudi pannello"
          >
            <X size={20} />
          </button>
        </div>
        <p className="text-sm text-gray-600">
          Descrivi l'obiettivo in poche parole. L'AI genererà un prompt dettagliato pronto per "Genera Flow"!
        </p>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Textarea obiettivo */}
        <div>
          <label className="block text-sm font-bold text-gray-900 mb-2">
            🎯 Obiettivo
          </label>
          <textarea
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            placeholder="Es: Creare un bot che aiuti gli utenti a prenotare appuntamenti"
            className="w-full border-2 border-amber-200 rounded-lg px-4 py-3 text-sm min-h-[120px] focus:border-amber-400 focus:ring-2 focus:ring-amber-200 transition"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={saveObjective}
              disabled={!objective.trim()}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition"
            >
              💾 Salva Obiettivo
            </button>
            <button
              onClick={handleGenerate}
              disabled={!objective.trim() || isGenerating}
              className={`flex-1 px-4 py-2 ${isGenerating ? 'bg-amber-300' : 'bg-amber-500 hover:bg-amber-600'} disabled:bg-gray-300 text-white text-sm font-bold rounded-lg transition flex items-center justify-center gap-2`}
            >
              {isGenerating ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Generazione in corso...
                </>
              ) : (
                <>
                  <Lightbulb className="w-4 h-4" />
                  Genera Prompt
                </>
              )}
            </button>
          </div>
        </div>

        {/* Prompt generato */}
        {generatedPrompt && (
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">
              ✨ Prompt Generato
            </label>
            <textarea
              readOnly
              value={generatedPrompt}
              className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-sm min-h-[180px] font-mono bg-gray-50"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={copyPrompt}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-semibold rounded-lg transition flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copia
              </button>
              <button
                onClick={useInGenerate}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold rounded-lg transition flex items-center justify-center gap-2"
              >
                <ArrowRight className="w-4 h-4" />
                Usa in Genera Flow
              </button>
            </div>
          </div>
        )}

        {/* Libreria Obiettivi Salvati */}
        {savedObjectives.length > 0 && (
          <div className="border-t-2 border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <FolderOpen className="w-4 h-4" />
                Obiettivi Salvati ({savedObjectives.length})
              </h3>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'name')}
                className="px-2 py-1 text-xs border rounded"
              >
                <option value="date">📅 Per data</option>
                <option value="name">🔤 Per nome</option>
              </select>
            </div>
            <div className="space-y-2">
              {sortedObjectives.map(obj => (
                <div
                  key={obj.id}
                  className="bg-amber-50 rounded-lg p-3 border-2 border-amber-200 hover:border-amber-400 transition cursor-pointer group"
                  onClick={() => loadObjective(obj.objective)}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1">
                      <div className="text-sm font-bold text-amber-900">{obj.name}</div>
                      <div className="text-xs text-gray-500">{new Date(obj.timestamp).toLocaleString('it-IT')}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteObjective(obj.id, obj.name);
                      }}
                      className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition opacity-0 group-hover:opacity-100"
                      title="Elimina obiettivo"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-gray-700 line-clamp-2">
                    {obj.objective}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

